import { type Config } from 'tailwindcss'

export const extendedTheme = {
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--primary))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--secondary))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
		progress: {
			DEFAULT: 'hsl(var(--progress))',
			foreground: 'hsl(var(--progress-foreground))',
		},
		text: {
			DEFAULT: 'hsl(var(--text-primary))',
		},
		// ── DNA design tokens (Option A · Editorial) ──────────────────
		paper: { DEFAULT: '#F5F1E8', 2: '#EDE6D8' },
		surface: '#FBF9F4',
		ink: { DEFAULT: '#1B1915', 2: '#3A362F' },
		stone: '#6F685C',
		line: { DEFAULT: '#D9D1C1', 2: '#E6DFD1' },
		brown: { DEFAULT: '#8A5A2B', soft: '#EBDCC8', light: '#C89A62' },
		ok: { DEFAULT: '#3F6B45', soft: '#DCE8D9' },
		warn: { DEFAULT: '#9A6A12', soft: '#F2E4C4', dot: '#C49A3A' },
		bad: { DEFAULT: '#A03A2C', soft: '#F3D9D3' },
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 1px)',
		sm: '3px',
		DEFAULT: '4px',
	},
	letterSpacing: {
		label: '.12em',
		wide2: '.14em',
		tightish: '-.01em',
		display: '-.02em',
	},
	transitionTimingFunction: {
		out: 'cubic-bezier(.2, .7, .2, 1)',
	},
	fontSize: {
		// ── DNA scale (serif display sizes pair with font-serif) ───────
		/** clamp 64→128px display (page heads, hero) */
		display: ['clamp(64px, 8vw, 128px)', { lineHeight: '.9', letterSpacing: '-.02em' }],
		/** clamp 48→88px title */
		title: ['clamp(48px, 6vw, 88px)', { lineHeight: '.95', letterSpacing: '-.015em' }],
		/** clamp 36→56px section heading */
		heading: ['clamp(36px, 4vw, 56px)', { lineHeight: '1', letterSpacing: '-.01em' }],
		/** 44px admin page title */
		'admin-title': ['44px', { lineHeight: '1', letterSpacing: '-.01em' }],
		/** 32px card heading */
		subheading: ['32px', { lineHeight: '1.05' }],
		/** 18px lead copy */
		lead: ['18px', { lineHeight: '1.6' }],
		/** 13.5px dense UI (admin) */
		ui: ['13.5px', { lineHeight: '1.45' }],
		/** 11px mono label */
		label: ['11px', { lineHeight: '1.4', letterSpacing: '.12em' }],
		/** 10px mono micro label */
		micro: ['10px', { lineHeight: '1.4', letterSpacing: '.14em' }],
		// legacy scale (old routes) ↓
		// 1rem = 16px
		/** 80px size / 84px high / bold */
		mega: ['5rem', { lineHeight: '5.25rem', fontWeight: '700' }],
		/** 56px size / 62px high / bold */
		h1: ['3.5rem', { lineHeight: '3.875rem', fontWeight: '700' }],
		/** 40px size / 48px high / bold */
		h2: ['2.5rem', { lineHeight: '3rem', fontWeight: '700' }],
		/** 32px size / 36px high / bold */
		h3: ['2rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 28px size / 36px high / bold */
		h4: ['1.75rem', { lineHeight: '2.25rem', fontWeight: '700' }],
		/** 24px size / 32px high / bold */
		h5: ['1.5rem', { lineHeight: '2rem', fontWeight: '700' }],
		/** 16px size / 20px high / bold */
		h6: ['1rem', { lineHeight: '1.25rem', fontWeight: '700' }],

		/** 32px size / 36px high / normal */
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		/** 28px size / 36px high / normal */
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		/** 24px size / 32px high / normal */
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		/** 20px size / 28px high / normal */
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		/** 16px size / 20px high / normal */
		'body-sm': ['1rem', { lineHeight: '1.25rem' }],
		/** 14px size / 18px high / normal */
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		/** 12px size / 16px high / normal */
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
	keyframes: {
		pop: { from: { opacity: '0', transform: 'translateY(8px) scale(.98)' } },
		slidein: { from: { opacity: '0', transform: 'translateX(40px)' } },
		fadeup: { from: { opacity: '0', transform: 'translateY(12px)' } },
		toastin: { from: { opacity: '0', transform: 'translateY(12px)' } },
		'accordion-down': {
			from: { height: '0' },
			to: { height: 'var(--radix-accordion-content-height)' },
		},
		'accordion-up': {
			from: { height: 'var(--radix-accordion-content-height)' },
			to: { height: '0' },
		},
	},
	animation: {
		pop: 'pop .25s cubic-bezier(.2,.7,.2,1)',
		slidein: 'slidein .3s cubic-bezier(.2,.7,.2,1)',
		fadeup: 'fadeup .6s cubic-bezier(.2,.7,.2,1) both',
		toastin: 'toastin .3s cubic-bezier(.2,.7,.2,1)',
		'accordion-down': 'accordion-down 0.2s ease-out',
		'accordion-up': 'accordion-up 0.2s ease-out',
	},
} satisfies Config['theme']
